/**
 * Firebase Cloud Messaging Service
 * Handles push notification functionality including:
 * - Token management
 * - Permission requests
 * - Foreground message handling
 * - Token storage in Firestore
 */

import { getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, updateDoc, getDoc, deleteField } from 'firebase/firestore';
import { messaging, db, auth } from '../firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/**
 * Check if push notifications are supported in this browser
 */
export const isNotificationSupported = () => {
    return 'Notification' in window && 'serviceWorker' in navigator && messaging !== null;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = () => {
    if (!isNotificationSupported()) return 'unsupported';
    return Notification.permission;
};

/**
 * Register service worker for push notifications
 */
export const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
        throw new Error('Service workers are not supported in this browser');
    }

    try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/'
        });

        console.log('Service Worker registered successfully:', registration);
        return registration;
    } catch (error) {
        console.error('Service Worker registration failed:', error);
        throw error;
    }
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async () => {
    if (!isNotificationSupported()) {
        throw new Error('Notifications are not supported in this browser');
    }

    try {
        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
            console.log('Notification permission granted');
            return true;
        } else {
            console.log('Notification permission denied');
            return false;
        }
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        throw error;
    }
};

/**
 * Get FCM token for this device
 */
export const getFCMToken = async () => {
    if (!isNotificationSupported() || !messaging) {
        throw new Error('Messaging is not supported');
    }

    try {
        // Ensure service worker is registered and ready
        const registration = await registerServiceWorker();

        // Wait for service worker to be active
        if (registration.waiting) {
            await new Promise(resolve => {
                registration.waiting.addEventListener('statechange', (e) => {
                    if (e.target.state === 'activated') resolve();
                });
            });
        }

        // Additional wait to ensure service worker is fully ready
        await navigator.serviceWorker.ready;

        // Request permission if not already granted
        if (Notification.permission !== 'granted') {
            const granted = await requestNotificationPermission();
            if (!granted) {
                throw new Error('Notification permission not granted');
            }
        }

        // Get FCM token with proper service worker registration
        const currentToken = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: registration
        });

        if (currentToken) {
            console.log('✅ FCM Token obtained:', currentToken.substring(0, 20) + '...');
            return currentToken;
        } else {
            console.warn('⚠️ No FCM token available');
            return null;
        }
    } catch (error) {
        console.error('❌ Error getting FCM token:', error);
        throw error;
    }
};

/**
 * Save FCM token to Firestore user document
 * Includes retry logic to handle auth timing issues
 */
export const saveFCMTokenToFirestore = async (token, retries = 3) => {
    // Wait for auth to be ready if needed
    for (let attempt = 0; attempt < retries; attempt++) {
        const user = auth.currentUser;

        if (user) {
            try {
                const userRef = doc(db, 'users', user.uid);

                await updateDoc(userRef, {
                    fcmToken: token,
                    fcmTokenUpdatedAt: new Date().toISOString(),
                    notificationsEnabled: true
                });

                console.log('✅ FCM token saved to Firestore for user:', user.uid);
                return true;
            } catch (error) {
                console.error('❌ Error saving FCM token to Firestore:', error);
                throw error;
            }
        }

        // Auth not ready yet, wait and retry
        if (attempt < retries - 1) {
            console.log(`⏳ Waiting for auth... (attempt ${attempt + 1}/${retries})`);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        }
    }

    // After all retries, still no user
    throw new Error('User must be logged in to save FCM token. Please ensure you are logged in and try again.');
};

/**
 * Remove FCM token from Firestore (on logout)
 */
export const removeFCMTokenFromFirestore = async () => {
    const user = auth.currentUser;

    if (!user) {
        return; // Already logged out
    }

    try {
        const userRef = doc(db, 'users', user.uid);

        await updateDoc(userRef, {
            fcmToken: deleteField(),
            fcmTokenUpdatedAt: deleteField()
        });

        console.log('FCM token removed from Firestore');
    } catch (error) {
        console.error('Error removing FCM token from Firestore:', error);
    }
};

/**
 * Get user's notification preferences from Firestore
 */
export const getNotificationPreferences = async () => {
    const user = auth.currentUser;

    if (!user) {
        return null;
    }

    try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
            const data = userDoc.data();
            return {
                enabled: data.notificationsEnabled !== false,
                preferences: data.notificationPreferences || {
                    likes: true,
                    comments: true,
                    mentions: true,
                    followers: true,
                    messages: true
                }
            };
        }

        return {
            enabled: false,
            preferences: {
                likes: true,
                comments: true,
                mentions: true,
                followers: true,
                messages: true
            }
        };
    } catch (error) {
        console.error('Error getting notification preferences:', error);
        return null;
    }
};

/**
 * Update user's notification preferences in Firestore
 */
export const updateNotificationPreferences = async (preferences) => {
    const user = auth.currentUser;

    if (!user) {
        throw new Error('User must be logged in to update preferences');
    }

    try {
        const userRef = doc(db, 'users', user.uid);

        await updateDoc(userRef, {
            notificationPreferences: preferences,
            notificationPreferencesUpdatedAt: new Date().toISOString()
        });

        console.log('Notification preferences updated');
    } catch (error) {
        console.error('Error updating notification preferences:', error);
        throw error;
    }
};

/**
 * Initialize FCM for logged-in user
 * Call this after successful login
 * Returns unsubscribe function for foreground listener cleanup
 */
export const initializeFCM = async () => {
    if (!isNotificationSupported() || !messaging) {
        console.warn('⚠️ FCM not supported in this environment');
        return { success: false, unsubscribe: null };
    }

    // Verify user is logged in
    if (!auth.currentUser) {
        console.warn('⚠️ Cannot initialize FCM: User not logged in');
        return { success: false, unsubscribe: null };
    }

    try {
        console.log('🔔 Initializing FCM for user:', auth.currentUser.uid);

        // Check if user has already granted permission
        if (Notification.permission === 'granted') {
            const token = await getFCMToken();
            if (token) {
                await saveFCMTokenToFirestore(token);
                const unsubscribe = setupForegroundMessageListener();
                console.log('✅ FCM initialized successfully');
                return { success: true, unsubscribe };
            }
        } else {
            console.log('ℹ️ Notification permission not granted yet');
        }

        return { success: false, unsubscribe: null };
    } catch (error) {
        console.error('❌ Error initializing FCM:', error);
        return { success: false, unsubscribe: null };
    }
};

/**
 * Setup listener for foreground messages
 * These are notifications received when the app is open
 * Returns unsubscribe function for cleanup
 */
export const setupForegroundMessageListener = () => {
    if (!messaging) {
        console.warn('⚠️ Cannot setup foreground listener: messaging not initialized');
        return null;
    }

    console.log('📱 Setting up foreground message listener');

    // onMessage returns an unsubscribe function
    const unsubscribe = onMessage(messaging, (payload) => {
        console.log('📬 Foreground message received:', payload);

        const notificationTitle = payload.notification?.title || 'New Notification';
        const notificationOptions = {
            body: payload.notification?.body || '',
            icon: payload.notification?.icon || '/favicon-192x192.png',
            badge: '/favicon-192x192.png',
            data: payload.data || {},
            tag: payload.data?.notificationId || 'default',
            requireInteraction: false
        };

        // Show browser notification even when app is in foreground
        if (Notification.permission === 'granted') {
            const notification = new Notification(notificationTitle, notificationOptions);

            notification.onclick = (event) => {
                event.preventDefault();
                const url = payload.data?.url || '/community';
                // Open in same window instead of new tab
                window.location.href = url;
                notification.close();
            };
        }
    });

    return unsubscribe;
};

/**
 * Request permission and initialize FCM
 * Use this when user explicitly clicks "Enable Notifications"
 */
export const enableNotifications = async () => {
    if (!isNotificationSupported()) {
        throw new Error('Notifications are not supported in this browser');
    }

    try {
        const granted = await requestNotificationPermission();

        if (!granted) {
            return { success: false, unsubscribe: null };
        }

        const token = await getFCMToken();

        if (token) {
            await saveFCMTokenToFirestore(token);
            const unsubscribe = setupForegroundMessageListener();
            console.log('✅ Notifications enabled successfully');
            return { success: true, unsubscribe };
        }

        return { success: false, unsubscribe: null };
    } catch (error) {
        console.error('❌ Error enabling notifications:', error);
        throw error;
    }
};

/**
 * Disable notifications for this user
 */
export const disableNotifications = async () => {
    try {
        await removeFCMTokenFromFirestore();

        const user = auth.currentUser;
        if (user) {
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                notificationsEnabled: false
            });
        }

        return true;
    } catch (error) {
        console.error('Error disabling notifications:', error);
        throw error;
    }
};

/**
 * Send a test notification (for testing purposes)
 */
export const sendTestNotification = () => {
    if (Notification.permission === 'granted') {
        new Notification('Test Notification', {
            body: 'This is a test notification from CrashCabin!',
            icon: '/favicon-192x192.png',
            badge: '/favicon-192x192.png'
        });
    } else {
        console.warn('Notification permission not granted');
    }
};
